import React, { useState, useEffect } from 'react';
import {
    Form, Input, Button, Space, message, Divider, Tabs, Select, Switch, Modal,
} from 'antd';
import {
    PlusOutlined, SaveOutlined, CloseOutlined,
} from '@ant-design/icons';
import { RateData } from '@/AppRedux/actions/admin/rateDetailConfig/rateDetailConfigType';
import TiptapEditor from '@/components/ui/TiptapEditor';

const { TabPane } = Tabs;

type RateFormProps = {
    mode: 'create' | 'edit';
    initialData?: RateData;
    onSubmit: (values: RateData) => Promise<void> | void;
    onCancel?: () => void;
    loading?: boolean;
}

const allSupportedLanguages: { [key: string]: string } = {
    en: "English",
    th: "Thai",
    zh: "Chinese",
    ru: "Russian",
    ko: "Korean",
    ja: "Japanese",
};

const RateForm: React.FC<RateFormProps> = ({
    mode,
    initialData,
    onSubmit,
    onCancel,
    loading = false,
}) => {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);

    // State for dynamic tabs and language management
    const [editableLangs, setEditableLangs] = useState<string[]>(['en']);
    const [activeLang, setActiveLang] = useState('en');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [langToAdd, setLangToAdd] = useState<string | null>(null);

    // State for enabled toggle
    const [enabled, setEnabled] = useState<boolean>(true);

    // Initialize form with data when in edit mode
    useEffect(() => {
        if (mode === 'edit' && initialData) {
            form.setFieldsValue({
                rate_code: initialData.rate_code,
            });

            const initialContents: { [key: string]: any } = {};
            // Make sure we have 'en' and any other languages from the data
            const initialEditableLangs = Array.from(new Set(['en', ...initialData.contents.map(c => c.lang).filter(Boolean)])).filter((lang): lang is string => Boolean(lang));

            initialEditableLangs.forEach(lang => {
                const contentItem = initialData.contents.find(c => c.lang === lang);
                initialContents[lang] = {
                    display_name: contentItem?.display_name || '',
                    description: contentItem?.description || '',
                };
            });

            form.setFieldsValue({ contents: initialContents });
            setEditableLangs(initialEditableLangs);

            // Initialize enabled state from initialData
            if (typeof initialData.enabled === 'boolean') {
                setEnabled(initialData.enabled);
            }
        }
    }, [mode, initialData, form]);

    const handleAddLanguage = () => {
        if (langToAdd) {
            setEditableLangs(prev => [...prev, langToAdd]);
            setActiveLang(langToAdd);
            setIsModalVisible(false);
            setLangToAdd(null);
        }
    };

    const onFinishFailed = (errorInfo: any) => {
        const firstError = errorInfo.errorFields?.[0];
        if (!firstError) return;

        const fieldPath = firstError.name;
        const fieldMessage = firstError.errors?.[0];
        const isContentField = fieldPath.includes('contents');

        if (isContentField) {
            const errorLang = fieldPath[1];
            message.error(`Please complete all required fields in the "${allSupportedLanguages[errorLang] || errorLang}" tab.`);
            setActiveLang(errorLang);
        } else {
            message.error(fieldMessage || 'Please complete all required fields.');
        }

        form.scrollToField(fieldPath, { behavior: 'smooth', block: 'center' });
    };

    const handleCopyFromEn = () => {
        const enContent = form.getFieldValue(['contents', 'en']) || {};
        const next = {
            display_name: enContent.display_name || '',
            description: enContent.description || '',
        };
        const currentContents = form.getFieldValue('contents') || {};
        form.setFieldsValue({
            contents: {
                ...currentContents,
                [activeLang]: next,
            },
        });
    };

    const handleFinish = async (values: any) => {
        setSubmitting(true);

        try {
            const contentsPayload = editableLangs.map(lang => {
                const langContent = values.contents?.[lang] || {};

                return {
                    lang,
                    display_name: langContent.display_name || '',
                    description: langContent.description || '',
                };
            });

            const formData: RateData = {
                id: mode === 'edit' ? initialData?.id ?? null : null,
                rate_code: values.rate_code,
                name: initialData?.name || null,
                contents: contentsPayload,
                sort_order: initialData?.sort_order || 1, // แนบค่าเดิมกลับไป หรือให้เป็น 1 ในกรณีสร้างใหม่
                enabled: enabled,
            };

            await onSubmit(formData);

        } catch (error) {
            console.error('Failed to submit form:', error);
            const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred.';
            message.error({ content: `Submission failed: ${errorMessage}`, duration: 5 });
        } finally {
            setSubmitting(false);
        }
    };

    const availableLanguagesToAdd = Object.keys(allSupportedLanguages).filter(l => !editableLangs.includes(l));

    return (
        <>
            <Form
                form={form}
                layout="vertical"
                onFinish={handleFinish}
                onFinishFailed={onFinishFailed}
                autoComplete="off"
                scrollToFirstError={true}
            >
                <Divider>Basic Information</Divider>
                <Form.Item
                    label="Rate Code"
                    name="rate_code"
                    rules={[{ required: true, message: 'Please enter rate code' }]}
                >
                    <Input placeholder="e.g., BE-LSTMN-RO" style={{ textTransform: 'uppercase' }} disabled={mode === 'edit'} />
                </Form.Item>

                {/* Enabled Toggle */}
                <Form.Item label="Rate Status">
                    <Space align="center">
                        <Switch
                            checked={enabled}
                            onChange={setEnabled}
                            checkedChildren="Enabled"
                            unCheckedChildren="Disabled"
                        />
                        <span style={{ color: enabled ? '#52c41a' : '#ff4d4f', fontWeight: 500 }}>
                            {enabled ? 'This rate is active and available for booking' : 'This rate is currently inactive'}
                        </span>
                    </Space>
                </Form.Item>

                <div style={{ display: "block", marginTop: 24 }}>
                    <Divider>Rate Content (Multilingual)</Divider>
                </div>

                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    {activeLang !== 'en' && (
                        <Button onClick={handleCopyFromEn}>
                            Copy from EN
                        </Button>
                    )}
                    <Button icon={<PlusOutlined />} onClick={() => setIsModalVisible(true)} disabled={availableLanguagesToAdd.length === 0}>
                        Add Language
                    </Button>
                </div>

                <Tabs activeKey={activeLang} onChange={setActiveLang}>
                    {editableLangs.map(lang => (
                        <TabPane tab={allSupportedLanguages[lang] || lang} key={lang} forceRender>
                            <Form.Item
                                label={`Display Name`}
                                name={['contents', lang, 'display_name']}
                                rules={[{ required: true, message: 'Please enter display name' }]}
                            >
                                <Input placeholder="e.g., Last Minute Promotion" />
                            </Form.Item>
                            <Form.Item
                                name={['contents', lang, 'description']}
                                label={`Description (HTML supported)`}
                                rules={[{ required: true, message: 'Please enter description' }]}
                                valuePropName="value"
                                trigger="onChange"
                            >
                                <TiptapEditor minHeight={160} disabled={false} />
                            </Form.Item>
                        </TabPane>
                    ))}
                </Tabs>

                <Divider />
                <Form.Item>
                    <Space size="middle">
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={submitting || loading}
                            icon={<SaveOutlined />}
                            size="large"
                        >
                            {mode === 'create' ? 'Create Rate' : 'Update Rate'}
                        </Button>
                        {mode === 'edit' && onCancel && (
                            <Button onClick={onCancel} icon={<CloseOutlined />} size="large">
                                Cancel
                            </Button>
                        )}
                    </Space>
                </Form.Item>
            </Form>

            <Modal
                title="Add Language"
                open={isModalVisible}
                onOk={handleAddLanguage}
                onCancel={() => setIsModalVisible(false)}
                okButtonProps={{ disabled: !langToAdd }}
            >
                <Select
                    placeholder="Select a language"
                    style={{ width: '100%' }}
                    onChange={setLangToAdd}
                    value={langToAdd}
                    options={availableLanguagesToAdd.map((l) => {
                        return {
                            label: allSupportedLanguages[l],
                            value: l,
                        }
                    })}
                >
                </Select>
            </Modal>
        </>
    );
};

export default RateForm;
